import { 
  ExtensionMessageType, 
  type ExtensionMessage, 
  type ExtensionResponse,
  type ExtensionSuccessCallback,
  type ExtensionErrorCallback
} from '@/types/auth'

/*
 * Shared extension constants and helpers
 */
export const EXTENSION_ID = process.env.NEXT_PUBLIC_EXTENSION_ID || 'mdoaagdccddnbjncalegjfemhnlijgbo'

export const EXTENSION_IFRAME_ID = 'fillapp-installed'
export const CHROME_WEB_STORE_URL = `https://chromewebstore.google.com/detail/${EXTENSION_ID}`

export function getChromeWebStoreUrl(): string {
  return CHROME_WEB_STORE_URL
}

// Session storage key for gating the install prompt (shown once per session)
export const SESSION_KEY_EXTENSION_PROMPT_SHOWN = 'fillapp:extension-install-prompt-shown'

export function wasInstallPromptShownThisSession(): boolean {
  if (typeof window === 'undefined') return true
  try {
    return window.sessionStorage.getItem(SESSION_KEY_EXTENSION_PROMPT_SHOWN) === '1'
  } catch {
    return true
  }
}

export function markInstallPromptShownThisSession(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(SESSION_KEY_EXTENSION_PROMPT_SHOWN, '1')
  } catch {
    // no-op
  }
}

/**
 * Extension communication errors
 */
export enum ExtensionError {
  NOT_AVAILABLE = 'Extension API not available',
  NOT_INSTALLED = 'Extension not installed or enabled',
  CONNECTION_FAILED = 'Could not establish connection',
  NO_RESPONSE = 'Extension did not respond',
  INVALID_RESPONSE = 'Invalid response from extension'
}

/**
 * Check if Chrome extension API is available
 */
function isChromeExtensionAPIAvailable(): boolean {
  return !!(
    typeof chrome !== 'undefined' &&
    chrome.runtime &&
    chrome.runtime.sendMessage
  )
}

/**
 * Lightweight runtime detection to check whether the FillApp extension
 * has injected its iframe into the page. This mirrors the check used in
 * UI components and should remain extremely fast and side‑effect free.
 */
export function detectExtensionInstalled(): boolean {
  return !!document.getElementById(EXTENSION_IFRAME_ID)
}

/**
 * Send authentication token to browser extension
 */
export async function sendTokenToExtension(
  extensionId: string,
  customToken: string,
  onSuccess: ExtensionSuccessCallback,
  onError: ExtensionErrorCallback,
  options?: { closeTab?: boolean }
): Promise<void> {
  // Check if Chrome extension API is available
  if (!isChromeExtensionAPIAvailable()) {
    const errorMessage = ExtensionError.NOT_AVAILABLE + 
      '. Please ensure the FillApp extension is installed and enabled.'
    onError(errorMessage)
    return
  }

  try {
    const message: ExtensionMessage = {
      type: ExtensionMessageType.AUTH_TOKEN,
      token: customToken,
      closeTab: options?.closeTab
    }

    // Send message to extension
    chrome.runtime.sendMessage(extensionId, message, (response: ExtensionResponse) => {
      // Check for Chrome runtime errors
      if (chrome.runtime.lastError) {
        console.error('Chrome runtime error:', chrome.runtime.lastError)
        
        const errorMessage = chrome.runtime.lastError.message || 'Unknown error'
        let displayError = `${ExtensionError.CONNECTION_FAILED}: ${errorMessage}`
        
        // Provide more specific error messages
        if (errorMessage.includes('Could not establish connection')) {
          displayError = ExtensionError.NOT_INSTALLED + 
            '. Please install and enable the FillApp extension, then try again.'
        } else if (errorMessage.includes('Receiving end does not exist')) {
          displayError = ExtensionError.NOT_INSTALLED + 
            '. The extension may have been disabled or uninstalled.'
        }
        
        onError(displayError)
        return
      }

      // Validate response
      if (!response) {
        onError(ExtensionError.NO_RESPONSE + '. Please try again.')
        return
      }

      if (typeof response !== 'object' || typeof response.success !== 'boolean') {
        onError(ExtensionError.INVALID_RESPONSE + '. Please contact support.')
        return
      }

      // Handle response
      if (response.success) {
        onSuccess()
      } else {
        const errorMessage = response.message || 'Unknown extension error'
        console.error('Extension reported failure:', errorMessage)
        onError(`Extension error: ${errorMessage}`)
      }
    })
  } catch (error) {
    console.error('Error sending message to extension:', error)
    const errorMessage = error instanceof Error ? error.message : String(error)
    onError(`Communication error: ${errorMessage}`)
  }
}

/**
 * Get extension ID from URL parameters
 */
export function getExtensionIdFromURL(searchParams: URLSearchParams): string | null {
  return searchParams.get('extensionId')
}

/**
 * Determine if the current auth flow was initiated on first install
 */
export function isFirstInstallFromURL(searchParams: URLSearchParams): boolean {
  const value = searchParams.get('isFirstInstall')
  return value === 'true'
}

/**
 * Check if current context supports extension communication
 */
export function isExtensionCommunicationSupported(): boolean {
  return typeof window !== 'undefined' && isChromeExtensionAPIAvailable()
}
